import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { auth, firestore } from "../../firebaseConfig";
import { doc, getDoc,updateDoc } from "firebase/firestore";
import Header from "../Homepage/Components/Header";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Link, useNavigate } from "react-router-dom";



const ManageSubscriptionPage = () => {
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isPaidUser, setIsPaidUser] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [cardDetails, setCardDetails] = useState(null);
  const [nextBillingDate, setNextBillingDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isValueUser, setIsValueUser] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate(); // To navigate to manage subscription page


  const handleUpdatePassword = () => {
    navigate("/UpdatePassword"); // Navigate to the password update page
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(firestore, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserDetails(userData);
          setIsPaidUser(userData.isPaidUserFlag || false);
          setIsValueUser(userData.value || false);
          setCustomerId(userData.customer_id || '')
          setSubscriptionId(userData.subscription_id || '')

          if (userData.isPaidUserFlag) {
            fetchBillingDetails(userData.customer_id, userData.subscription_id);
          }
        }
      } else {
        setUser(null);
        setIsPaidUser(null);
        setCardDetails(null);
        setNextBillingDate(null);
        setCustomerId('')
        setSubscriptionId('')
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isPaidUser]);

  const fetchBillingDetails = async (customerId, subscriptionId) => {
    try {
      const response = await fetch("https://app.stock101.org/get-billing-details", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customer_id: customerId,
          subscription_id: subscriptionId,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setCardDetails(data.card_details);
        setNextBillingDate(
          new Date(data.next_billing_date * 1000).toLocaleDateString()
        );
      } else {
        console.error("Error fetching billing details:", data.error);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getCardBrandLogo = (brand) => {
    const logos = {
      visa: "https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg",
      mastercard: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
      amex: "https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo_%282018%29.svg",
      discover: "https://upload.wikimedia.org/wikipedia/commons/0/0c/Discover_Card_logo.svg",
    };
    return logos[brand?.toLowerCase()] || "";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafc",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleCancelSubscription = async () => {
    try {
      const response = await fetch("https://app.stock101.org/cancel-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subscriptionId,
          customerId,
        }),
      });
  
      const data = await response.json();
      if (response.ok && data.success) {

  
        // Update the user's isPaidUserFlag in Firestore
        const userRef = doc(firestore, "users", user.uid); // Reference to the user's document
        await updateDoc(userRef, {
          isPaidUserFlag: false, // Set the paid flag to false
          subscription_id: "", // Clear the subscription ID
          planId: "", // Clear the plan ID
        });
  
        // Optional: Add any additional logic to update the UI or redirect the user
        console.log("User's subscription details updated in Firestore.");
      } else {
        alert(`Error: ${data.error || "Failed to cancel subscription"}`);
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      handleClose();
      navigate("/");  // Close any modal or dialog if applicable
    }
  };

  return (
    <Box>
      <Header />

      <Box
        sx={{
          padding: "32px",
          backgroundColor: "#f9fafc",
          
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
          fontFamily: "'Inter', sans-serif",
        }}
      >
   <Box sx={{ padding: "0px", maxWidth: "600px", margin: "auto" }}>
      {/* User Info Section */}
      <Box sx={{ textAlign: "center", marginBottom: "16px" }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "#333", marginBottom: "8px" }}
        >
          {userDetails?.name || "User"}
        </Typography>
        <Typography variant="body1" sx={{ color: "#666" }}>
          Email: {user?.email}
        </Typography>
      </Box>

      {/* Password Section */}
      <Box sx={{ marginBottom: "24px" }}>
        <Typography
          variant="h6"
          sx={{
            color: "#333",
            marginBottom: "0px",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        >
          
        </Typography>
        
        {/* Hidden Password Field */}
        <TextField
          label="Password"
          type="password"
          fullWidth
          value="********"
          disabled
          sx={{
            marginBottom: "16px",
            "& .MuiInputBase-root": {
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
            },
          }}
        />

        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={handleUpdatePassword}
          sx={{
            fontWeight: "bold",
            textTransform: "none",
            padding: "12px",
            borderRadius: "8px",
            borderColor: "#4CAF50",
            "&:hover": { borderColor: "#45A049", backgroundColor: "#f1f1f1" },
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          Update Password
        </Button>
      </Box>
    </Box>

        {/* Subscription Section */}
        <Card
          sx={{
            width: "100%",
            maxWidth: "500px",
            borderRadius: "12px",
            boxShadow: "0 6px 15px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#ffffff",
          }}
        >
          <CardContent sx={{ padding: "24px", textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: isPaidUser ? "#4caf50" : "#f44336",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {isPaidUser ? <CheckCircleIcon /> : <ErrorIcon />}
              {isPaidUser ? "Pro Plan" : "Free Trial"}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "#666",
                marginTop: "8px",
              }}
            >
              {isPaidUser
                ? "Enjoy full access to stock analytics and news."
                : "Limited to 5 stocks and 4 news articles."}
            </Typography>

            {!isPaidUser && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<UpgradeIcon />}
                sx={{
                  textTransform: "none",
                  padding: "12px 32px",
                  fontSize: "16px",
                  borderRadius: "24px",
                  marginTop: "16px",
                }}
                onClick={() => navigate("/PaymentForm")}
              >
                Upgrade to Pro ($8 USD/month)
              </Button>
            )}
          </CardContent>
        </Card>

        {isPaidUser && !isValueUser && (
          <Box
            sx={{
              padding: "24px",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 6px 15px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
              maxWidth: "500px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#4caf50",
                marginBottom: "8px",
              }}
            >
              Plan Details
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#666", marginBottom: "16px" }}
            >
              Next Billing Date: <strong>{nextBillingDate || "Loading..."}</strong>
            </Typography>

            {cardDetails && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <CreditCardIcon sx={{ color: "#666" }} />
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Card Ending: {cardDetails?.last4 || "****"}
                </Typography>
              </Box>
            )}

            <Box sx={{ marginTop: "24px", display: "flex", gap: "16px" }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                sx={{
                  textTransform: "none",
                  padding: "8px 16px",
                  fontSize: "14px",
                  borderRadius: "8px",
                }}
                onClick={handleOpen}
              >
                Cancel Subscription
              </Button>
            </Box>
            <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel your subscription? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            No
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            variant="contained"
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
          </Box>
        )}


{isPaidUser && isValueUser && (
  <Box
    sx={{
      marginTop: "32px",
      padding: "24px",
      borderRadius: "16px",
      backgroundColor: "#f0fdf8",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Celebration Icons */}


    <Typography
      variant="h5"
      sx={{
        fontWeight: "bold",
        fontFamily: "'Work Sans', sans-serif",
        color: "#2c7a7b",
        marginBottom: "8px",
        fontSize: "1.2rem",
      }}
    >
      🎉 Dear, {userDetails?.name}! 🎉
    </Typography>

    <Typography
      variant="body1"
      sx={{
        color: "#285e61",
        fontFamily: "'Work Sans', sans-serif",
        marginBottom: "16px",
        lineHeight: 1.6,
        fontSize: "1rem",
      }}
    >
      Thank you for being a valued customer and one of our early testers!  
      We're thrilled to offer you a <strong>lifetime free subscription</strong> to Stock101 as a token of our gratitude. 
      Your support means the world to us.
    </Typography>

    <Typography
      variant="body2"
      sx={{
        color: "#285e61",
        fontFamily: "'Work Sans', sans-serif",
        marginBottom: "16px",
        lineHeight: 1.6,
        fontSize: "0.9rem",
      }}
    >
      Enjoy exploring the best tools and insights Stock101 has to offer. We're excited to grow with your feedback and support!
    </Typography>

    {/* Signature */}
    <Typography
      variant="body2"
      sx={{
        color: "#2f855a",
        fontFamily: "'Work Sans', sans-serif",
        fontWeight: "bold",
        marginTop: "16px",
        fontSize: "0.9rem",
      }}
    >
      Regards,
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "#2f855a",
        fontFamily: "'Work Sans', sans-serif",
        fontWeight: "bold",
        fontSize: "0.9rem",
      }}
    >
      Abraham George <br />
      CEO, Stock101
    </Typography>
  </Box>
)}












      </Box>
    </Box>
  );
};

export default ManageSubscriptionPage;
